// BLUFFMANIA — fill the blank with a lie, fool your friends, find the truth.
// Authoritative engine: runs on the server (online rooms) or in the browser (demo / bots).

import { ADULT_CATS, BLUFF_CATS, type BluffItem } from "../content";
import {
  activePlayers, bool, clampInt, isError, oneOf, stringList,
  type BotPlan, type Engine, type EngineCtx, type TimedState,
} from "../engine";
import { capitalizeFirst, cleanText, matchesTruth, normalizeAnswer } from "../text";
import type { ErrorCode, GameResultSummary, Lang, RoomCore } from "../types";

export interface BluffSettings {
  length: "short" | "normal" | "long";
  cats: string[]; // empty = everything allowed
  adult: boolean;
  speed: "chill" | "normal" | "fast";
  houseLie: boolean;
  help: number; // "lie for me" uses per player per game
  jokes?: boolean; // the host voice cracks a joke now and then (2–3 per game); missing = on
}

type Phase = "intro" | "write" | "vote" | "reveal" | "scores" | "over";

interface Option {
  id: string;
  text: string;
  kind: "truth" | "player" | "house";
  owners: string[]; // player ids (player lies only)
  auto: boolean; // written by the house because the owner ran out of time
}

interface Stats {
  fooled: number; // how many times others fell for my lies
  truths: number; // truths found
  gullible: number; // times I fell for a lie
  likes: number; // likes received
}

export interface BluffState extends TimedState {
  phase: Phase;
  q: number;
  items: BluffItem[];
  mult: number[];
  acts: number[];
  active: string[];
  lies: Record<string, { text: string; auto: boolean; helped: boolean }>;
  suggestion: Record<string, string>;
  helpLeft: Record<string, number>;
  options: Option[];
  votes: Record<string, string>;
  likes: Record<string, string[]>;
  revealOrder: string[];
  revealStep: number;
  scores: Record<string, number>;
  prevScores: Record<string, number>;
  delta: Record<string, number>;
  stats: Record<string, Stats>;
  best: { text: string; by: string; fooled: number } | null;
  startedAt: number;
}

const LENGTHS = { short: [2, 2, 1], normal: [4, 3, 1], long: [5, 5, 1] } as const;
const SPEED = {
  chill: { write: 75_000, vote: 35_000 },
  normal: { write: 55_000, vote: 25_000 },
  fast: { write: 35_000, vote: 16_000 },
} as const;
const INTRO_MS = 3_200;
const ACT_INTRO_MS = 5_200;
// the very first intro leaves the host voice time to greet everyone before the first question
const FIRST_INTRO_MS = 9_000;
const REVEAL_LIE_MS = 4_600;
const REVEAL_TRUTH_MS = 6_000;
const SCORES_MS = 6_500;
const ACT_SCORES_MS = 9_000;
export const BLUFF_MAX_LIE = 40;

export const BLUFF_POINTS = { truth: 1000, fool: 500, autoFool: 250, like: 100 } as const;

const emptyStats = (): Stats => ({ fooled: 0, truths: 0, gullible: 0, likes: 0 });

function allowedCats(settings: BluffSettings): Set<string> {
  const base = settings.cats.length ? settings.cats : BLUFF_CATS.filter((c) => !ADULT_CATS.has(c));
  const set = new Set(base.filter((c) => settings.adult || !ADULT_CATS.has(c)));
  if (settings.adult && !settings.cats.length) set.add("night");
  return set;
}

function pickItems(room: RoomCore<BluffSettings>, ctx: EngineCtx, count: number): BluffItem[] | null {
  const all = ctx.content.bluff(room.lang);
  const cats = allowedCats(room.settings);
  let pool = all.filter((it) => cats.has(it.cat) && (room.settings.adult || !it.adult));
  if (pool.length < count) pool = all.filter((it) => room.settings.adult || !it.adult);
  if (pool.length < count) return null;
  const used = new Set(room.usedContent);
  const fresh = pool.filter((it) => !used.has(it.id));
  const source = fresh.length >= count ? fresh : pool;
  // Easier questions first, the final question is a hard one when available.
  const shuffled = ctx.rng.shuffle(source);
  const picked = shuffled.slice(0, count);
  const last = picked.reduce((best, it, i) => (it.diff > picked[best].diff ? i : best), picked.length - 1);
  const [finale] = picked.splice(last, 1);
  picked.sort((a, b) => a.diff - b.diff);
  picked.push(finale);
  return picked;
}

function resetQuestion(room: RoomCore<BluffSettings>, s: BluffState) {
  s.active = activePlayers(room).map((p) => p.id);
  s.lies = {};
  s.suggestion = {};
  s.options = [];
  s.votes = {};
  s.likes = {};
  s.revealOrder = [];
  s.revealStep = 0;
  s.delta = {};
  for (const id of s.active) {
    if (s.scores[id] === undefined) s.scores[id] = 0;
    if (!s.stats[id]) s.stats[id] = emptyStats();
    if (s.helpLeft[id] === undefined) s.helpLeft[id] = room.settings.help;
  }
}

function setPhase(s: BluffState, phase: Phase, now: number, ms: number | null) {
  s.phase = phase;
  s.deadline = ms === null ? null : now + ms;
  s.phaseMs = ms ?? 0;
}

function unusedHouseLie(s: BluffState, item: BluffItem, ctx: EngineCtx, extraTaken: string[] = []): string | null {
  const taken = new Set<string>([
    ...Object.values(s.lies).map((l) => normalizeAnswer(l.text)),
    ...Object.values(s.suggestion).map(normalizeAnswer),
    ...extraTaken.map(normalizeAnswer),
  ]);
  const candidates = item.lies.filter((l) => !taken.has(normalizeAnswer(l)) && !matchesTruth(l, item.a, item.alt));
  return candidates.length ? ctx.rng.pick(candidates) : null;
}

function goToVote(room: RoomCore<BluffSettings>, s: BluffState, ctx: EngineCtx) {
  const item = s.items[s.q];
  // Players who never submitted get an automatic house lie (half points if it fools anyone).
  for (const id of s.active) {
    if (!s.lies[id]) {
      const text = unusedHouseLie(s, item, ctx) ?? `${item.lies[0]}`;
      s.lies[id] = { text, auto: true, helped: false };
    }
  }
  const options: Option[] = [];
  const seen = new Map<string, Option>();
  const add = (text: string, kind: Option["kind"], owner: string | null, auto = false) => {
    const key = normalizeAnswer(text);
    const existing = seen.get(key);
    if (existing) {
      if (owner && existing.kind === "player") existing.owners.push(owner);
      return;
    }
    const opt: Option = { id: ctx.rng.id(5), text: capitalizeFirst(text), kind, owners: owner ? [owner] : [], auto };
    seen.set(key, opt);
    options.push(opt);
  };
  add(item.a, "truth", null);
  for (const id of s.active) add(s.lies[id].text, "player", id, s.lies[id].auto);
  if (room.settings.houseLie) {
    const house = unusedHouseLie(s, item, ctx, options.map((o) => o.text));
    if (house) add(house, "house", null);
  }
  s.options = ctx.rng.shuffle(options);
  setPhase(s, "vote", ctx.now, SPEED[room.settings.speed].vote);
}

function goToReveal(room: RoomCore<BluffSettings>, s: BluffState, ctx: EngineCtx) {
  const m = s.mult[s.q];
  s.prevScores = { ...s.scores };
  s.delta = {};
  const add = (id: string, pts: number) => {
    s.scores[id] = (s.scores[id] ?? 0) + pts;
    s.delta[id] = (s.delta[id] ?? 0) + pts;
  };
  const votesFor = (optId: string) => Object.entries(s.votes).filter(([, o]) => o === optId).map(([v]) => v);
  for (const opt of s.options) {
    const voters = votesFor(opt.id);
    if (opt.kind === "truth") {
      for (const v of voters) {
        add(v, BLUFF_POINTS.truth * m);
        s.stats[v] && s.stats[v].truths++;
      }
    } else {
      for (const v of voters) s.stats[v] && s.stats[v].gullible++;
      if (opt.kind === "player" && voters.length) {
        const per = (opt.auto ? BLUFF_POINTS.autoFool : BLUFF_POINTS.fool) * m;
        for (const owner of opt.owners) {
          add(owner, per * voters.length);
          if (s.stats[owner]) s.stats[owner].fooled += voters.length;
        }
        if (!opt.auto && (!s.best || voters.length > s.best.fooled)) {
          s.best = { text: opt.text, by: opt.owners[0], fooled: voters.length };
        }
      }
    }
  }
  const picked = s.options.filter((o) => o.kind !== "truth" && votesFor(o.id).length > 0);
  const order = ctx.rng.shuffle(picked).sort((a, b) => votesFor(a.id).length - votesFor(b.id).length);
  const truth = s.options.find((o) => o.kind === "truth")!;
  s.revealOrder = [...order.map((o) => o.id), truth.id];
  s.revealStep = 0;
  setPhase(s, "reveal", ctx.now, s.revealOrder.length === 1 ? REVEAL_TRUTH_MS : REVEAL_LIE_MS);
}

function finishQuestion(room: RoomCore<BluffSettings>, s: BluffState, ctx: EngineCtx) {
  // Likes are paid out at the end of the reveal (likes during the reveal count).
  for (const [liker, optIds] of Object.entries(s.likes)) {
    for (const optId of optIds) {
      const opt = s.options.find((o) => o.id === optId);
      if (!opt || opt.kind !== "player") continue;
      for (const owner of opt.owners) {
        if (owner === liker) continue;
        s.scores[owner] = (s.scores[owner] ?? 0) + BLUFF_POINTS.like;
        s.delta[owner] = (s.delta[owner] ?? 0) + BLUFF_POINTS.like;
        if (s.stats[owner]) s.stats[owner].likes++;
      }
    }
  }
  const actEnds = s.q + 1 >= s.items.length || s.acts[s.q + 1] !== s.acts[s.q];
  setPhase(s, "scores", ctx.now, actEnds ? ACT_SCORES_MS : SCORES_MS);
}

function nextQuestion(room: RoomCore<BluffSettings>, s: BluffState, ctx: EngineCtx) {
  if (s.q + 1 >= s.items.length) {
    setPhase(s, "over", ctx.now, null);
    return;
  }
  const newAct = s.acts[s.q + 1] !== s.acts[s.q];
  s.q += 1;
  resetQuestion(room, s);
  setPhase(s, "intro", ctx.now, newAct ? ACT_INTRO_MS : INTRO_MS);
}

function maybeAdvanceWrite(room: RoomCore<BluffSettings>, s: BluffState, ctx: EngineCtx) {
  if (s.phase === "write" && s.active.length > 0 && s.active.every((id) => s.lies[id])) goToVote(room, s, ctx);
}

function maybeAdvanceVote(room: RoomCore<BluffSettings>, s: BluffState, ctx: EngineCtx) {
  if (s.phase !== "vote") return;
  // Players whose only possible choices are their own lies can't vote; ignore them.
  const mustVote = s.active.filter((id) => s.options.some((o) => !o.owners.includes(id)));
  if (mustVote.every((id) => s.votes[id])) goToReveal(room, s, ctx);
}

export const bluffEngine: Engine<BluffState, BluffSettings> = {
  id: "bluff",
  minPlayers: 2,
  maxPlayers: 12,
  supportsBots: true,

  defaults(_lang: Lang): BluffSettings {
    return { length: "normal", cats: [], adult: false, speed: "normal", houseLie: true, help: 2, jokes: true };
  },

  sanitize(input: any, prev: BluffSettings): BluffSettings {
    const i = input && typeof input === "object" ? input : {};
    return {
      length: oneOf(i.length, ["short", "normal", "long"] as const, prev.length),
      cats: stringList(i.cats, BLUFF_CATS as unknown as string[], prev.cats),
      adult: bool(i.adult, prev.adult),
      speed: oneOf(i.speed, ["chill", "normal", "fast"] as const, prev.speed),
      houseLie: bool(i.houseLie, prev.houseLie),
      help: clampInt(i.help, 0, 3, prev.help),
      jokes: bool(i.jokes, prev.jokes ?? true),
    };
  },

  start(room, ctx) {
    const plan = LENGTHS[room.settings.length];
    const total = plan[0] + plan[1] + plan[2];
    const items = pickItems(room, ctx, total);
    if (!items) return "no_content" as ErrorCode;
    const acts: number[] = [];
    const mult: number[] = [];
    plan.forEach((n, act) => {
      for (let k = 0; k < n; k++) {
        acts.push(act);
        mult.push(act + 1);
      }
    });
    const s: BluffState = {
      phase: "intro", deadline: null, phaseMs: 0, q: 0, items, mult, acts,
      active: [], lies: {}, suggestion: {}, helpLeft: {}, options: [], votes: {}, likes: {},
      revealOrder: [], revealStep: 0, scores: {}, prevScores: {}, delta: {}, stats: {}, best: null,
      startedAt: ctx.now,
    };
    resetQuestion(room, s);
    setPhase(s, "intro", ctx.now, FIRST_INTRO_MS);
    room.usedContent.push(...items.map((i) => i.id));
    if (room.usedContent.length > 400) room.usedContent.splice(0, room.usedContent.length - 400);
    return s;
  },

  act(room, s, actorId, a, ctx) {
    if (!a || typeof a !== "object") return "bad_input";
    const isHost = room.hostId === actorId;
    const item = s.items[s.q];
    switch (a.t) {
      case "lie": {
        if (s.phase !== "write") return "bad_phase";
        if (!s.active.includes(actorId)) return "not_player";
        const text = cleanText(a.text, BLUFF_MAX_LIE);
        if (!text) return "bad_input";
        if (matchesTruth(text, item.a, item.alt)) return "is_truth";
        const norm = normalizeAnswer(text);
        if (!norm) return "bad_input";
        for (const [pid, lie] of Object.entries(s.lies)) {
          if (pid !== actorId && normalizeAnswer(lie.text) === norm) return "taken";
        }
        const helped = !!s.suggestion[actorId] && normalizeAnswer(s.suggestion[actorId]) === norm;
        s.lies[actorId] = { text, auto: false, helped };
        maybeAdvanceWrite(room, s, ctx);
        return s;
      }
      case "help": {
        if (s.phase !== "write") return "bad_phase";
        if (!s.active.includes(actorId)) return "not_player";
        if ((s.helpLeft[actorId] ?? 0) <= 0) return "no_help_left";
        const sug = unusedHouseLie(s, item, ctx);
        if (!sug) return "no_help_left";
        s.helpLeft[actorId] -= 1;
        s.suggestion[actorId] = sug;
        return s;
      }
      case "vote": {
        if (s.phase !== "vote") return "bad_phase";
        if (!s.active.includes(actorId)) return "not_player";
        if (s.votes[actorId]) return "already";
        const opt = s.options.find((o) => o.id === a.option);
        if (!opt) return "bad_input";
        if (opt.owners.includes(actorId)) return "own_option";
        s.votes[actorId] = opt.id;
        maybeAdvanceVote(room, s, ctx);
        return s;
      }
      case "like": {
        if (s.phase !== "vote" && s.phase !== "reveal") return "bad_phase";
        const opt = s.options.find((o) => o.id === a.option);
        if (!opt) return "bad_input";
        if (opt.owners.includes(actorId)) return "own_option";
        const mine = (s.likes[actorId] ??= []);
        const idx = mine.indexOf(opt.id);
        if (idx >= 0) mine.splice(idx, 1);
        else mine.push(opt.id);
        return s;
      }
      case "next": {
        if (!isHost) return "not_host";
        if (s.phase === "intro" || s.phase === "reveal" || s.phase === "scores") return bluffEngine.tick(room, s, ctx);
        return "bad_phase";
      }
      case "skip": {
        if (!isHost) return "not_host";
        if (s.phase === "write" || s.phase === "vote") return bluffEngine.tick(room, s, ctx);
        return "bad_phase";
      }
      default:
        return "bad_input";
    }
  },

  tick(room, s, ctx) {
    switch (s.phase) {
      case "intro":
        setPhase(s, "write", ctx.now, SPEED[room.settings.speed].write);
        break;
      case "write":
        goToVote(room, s, ctx);
        break;
      case "vote":
        goToReveal(room, s, ctx);
        break;
      case "reveal": {
        if (s.revealStep + 1 < s.revealOrder.length) {
          s.revealStep += 1;
          const last = s.revealStep === s.revealOrder.length - 1;
          setPhase(s, "reveal", ctx.now, last ? REVEAL_TRUTH_MS : REVEAL_LIE_MS);
        } else {
          finishQuestion(room, s, ctx);
        }
        break;
      }
      case "scores":
        nextQuestion(room, s, ctx);
        break;
      case "over":
        s.deadline = null;
        break;
    }
    return s;
  },

  onLeave(room, s, playerId, ctx) {
    s.active = s.active.filter((id) => id !== playerId);
    if (s.phase === "write") maybeAdvanceWrite(room, s, ctx);
    if (s.phase === "vote") maybeAdvanceVote(room, s, ctx);
    return s;
  },

  onJoin(_room, s, playerId) {
    if (s.scores[playerId] === undefined) s.scores[playerId] = 0;
    return s;
  },

  view(room, s, viewerId, now) {
    const item = s.items[s.q];
    const inReveal = s.phase === "reveal";
    const after = s.phase === "scores" || s.phase === "over";
    const showQuestion = s.phase !== "intro";
    const mine = viewerId ? s.lies[viewerId] : undefined;
    const votesFor = (optId: string) => Object.entries(s.votes).filter(([, o]) => o === optId).map(([v]) => v);
    const likeCount = (optId: string) => Object.values(s.likes).filter((l) => l.includes(optId)).length;

    const options =
      s.phase === "vote" || inReveal || after
        ? s.options.map((o) => ({
            id: o.id,
            text: o.text,
            mine: !!viewerId && o.owners.includes(viewerId),
            liked: !!viewerId && (s.likes[viewerId] ?? []).includes(o.id),
          }))
        : [];

    const revealUpTo = inReveal ? s.revealStep : after ? s.revealOrder.length - 1 : -1;
    const reveal = s.revealOrder.slice(0, revealUpTo + 1).map((optId) => {
      const o = s.options.find((x) => x.id === optId)!;
      const voters = votesFor(o.id);
      const m = s.mult[s.q];
      const pts =
        o.kind === "truth" ? BLUFF_POINTS.truth * m : o.kind === "player" ? (o.auto ? BLUFF_POINTS.autoFool : BLUFF_POINTS.fool) * m * voters.length : 0;
      return { id: o.id, text: o.text, kind: o.kind, owners: o.owners, auto: o.auto, voters, points: pts, likes: likeCount(o.id) };
    });

    const truthShown = reveal.some((r) => r.kind === "truth");
    const ranking = Object.keys(s.scores)
      .filter((id) => room.players.some((p) => p.id === id))
      .sort((a, b) => (s.scores[b] ?? 0) - (s.scores[a] ?? 0));

    let awards: { key: string; id: string; value: number; text?: string }[] | null = null;
    if (s.phase === "over") {
      const top = (k: keyof Stats) => {
        const best = Object.entries(s.stats).filter(([id]) => room.players.some((p) => p.id === id)).sort((a, b) => b[1][k] - a[1][k])[0];
        return best && best[1][k] > 0 ? { id: best[0], value: best[1][k] } : null;
      };
      awards = [];
      const liar = top("fooled");
      if (liar) awards.push({ key: "liar", ...liar });
      const truth = top("truths");
      if (truth) awards.push({ key: "detective", ...truth });
      const gull = top("gullible");
      if (gull) awards.push({ key: "gullible", ...gull });
      const liked = top("likes");
      if (liked) awards.push({ key: "liked", ...liked });
      if (s.best) awards.push({ key: "bestLie", id: s.best.by, value: s.best.fooled, text: s.best.text });
    }

    return {
      phase: s.phase,
      deadline: s.deadline,
      phaseMs: s.phaseMs,
      q: s.q,
      total: s.items.length,
      act: s.acts[s.q],
      acts: s.acts,
      mult: s.mult[s.q],
      cat: item.cat,
      qid: item.id,
      question: showQuestion ? item.q : null,
      answer: truthShown || after ? capitalizeFirst(item.a) : null,
      isActive: !!viewerId && s.active.includes(viewerId),
      active: s.active,
      myLie: mine && !mine.auto ? mine.text : null,
      myLieAuto: !!mine?.auto,
      suggestion: viewerId ? s.suggestion[viewerId] ?? null : null,
      helpLeft: viewerId ? s.helpLeft[viewerId] ?? 0 : 0,
      submitted: s.phase === "write" ? Object.keys(s.lies) : s.active,
      options,
      myVote: viewerId ? s.votes[viewerId] ?? null : null,
      voted: s.phase === "vote" ? Object.keys(s.votes) : [],
      reveal,
      revealStep: inReveal ? s.revealStep : null,
      revealTotal: s.revealOrder.length,
      scores: inReveal ? s.prevScores : s.scores,
      delta: after ? s.delta : {},
      ranking,
      awards,
      now,
    };
  },

  bot(room, s, botId, ctx): BotPlan | null {
    if (!s.active.includes(botId)) return null;
    const item = s.items[s.q];
    if (s.phase === "write" && !s.lies[botId]) {
      const text = unusedHouseLie(s, item, ctx);
      if (!text) return null;
      return { key: `write:${s.q}`, delay: 2500 + ctx.rng.int(9000), action: { t: "lie", text } };
    }
    if (s.phase === "vote" && !s.votes[botId]) {
      const options = s.options.filter((o) => !o.owners.includes(botId));
      if (!options.length) return null;
      const truthChance = item.diff === 1 ? 0.5 : item.diff === 2 ? 0.35 : 0.22;
      const truth = options.find((o) => o.kind === "truth");
      const choice = truth && ctx.rng.next() < truthChance ? truth : ctx.rng.pick(options);
      return { key: `vote:${s.q}`, delay: 2000 + ctx.rng.int(7000), action: { t: "vote", option: choice.id } };
    }
    return null;
  },

  isOver(s) {
    return s.phase === "over";
  },

  summary(room, s): GameResultSummary {
    const players = room.players
      .filter((p) => s.scores[p.id] !== undefined)
      .map((p) => ({ id: p.id, name: p.name, score: s.scores[p.id] ?? 0, userId: p.userId ?? null, isBot: p.isBot }));
    const max = Math.max(0, ...players.map((p) => p.score));
    return { game: "bluff", lang: room.lang, players, winnerIds: players.filter((p) => p.score === max && max > 0).map((p) => p.id) };
  },
};

// Re-export for tests
export { isError };
